import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiUrl } from '@/config/api';
import { useAuth } from '@/context/AuthContext.jsx';
import useLanguageNavigation from '@/hooks/useLanguageNavigation.js';

const defaultStats = {
  referralCode: '',
  invitedCount: 0,
  totalReferralEur: 0,
};

const ReferralProgramPage = () => {
  const { t, i18n } = useTranslation('dashboard');
  const { token } = useAuth();
  const { getLocalizedPath } = useLanguageNavigation();

  const [stats, setStats] = useState(defaultStats);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copyMessage, setCopyMessage] = useState('');
  const [copyError, setCopyError] = useState('');

  useEffect(() => {
    const loadStats = async () => {
      if (!token) {
        setStats(defaultStats);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const response = await fetch(apiUrl('/api/referral/stats'), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const responseData = await response.json().catch(() => ({}));
          throw new Error(responseData.error || t('referral.errors.load'));
        }

        const data = await response.json();
        setStats({
          referralCode: data?.referralCode || '',
          invitedCount: Number(data?.invitedCount) || 0,
          totalReferralEur: Number(data?.totalReferralEur) || 0,
        });
      } catch (err) {
        setError(err.message);
        setStats(defaultStats);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [t, token]);

  const referralLink = useMemo(() => {
    if (!stats.referralCode) {
      return '';
    }
    const path = getLocalizedPath('/register');
    const url = new URL(path, window.location.origin);
    url.searchParams.set('ref', stats.referralCode);
    return url.toString();
  }, [getLocalizedPath, stats.referralCode]);

  const handleCopy = async () => {
    if (!referralLink) {
      return;
    }
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopyMessage(t('referral.messages.copied'));
      setCopyError('');
    } catch (copyErr) {
      console.error('[REFERRAL_COPY_FAILED]', copyErr);
      setCopyMessage('');
      setCopyError(t('referral.messages.copyError'));
    }
  };

  const formatEur = useMemo(() => {
    try {
      return new Intl.NumberFormat(i18n.language || 'ua', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    } catch (error) {
      return new Intl.NumberFormat('en', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    }
  }, [i18n.language]);

  return (
    <div className="page-card referral-page">
      <div className="page-card-header">
        <div>
          <p className="page-caption">{t('referral.caption')}</p>
          <h1>{t('referral.title')}</h1>
          <p className="page-description">{t('referral.description')}</p>
        </div>
      </div>

      <div className="referral-link-block">
        <label className="referral-label" htmlFor="referral-link">
          {t('referral.linkLabel')}
          <div className="referral-input-row">
            <input
              id="referral-link"
              type="text"
              readOnly
              value={referralLink || t('referral.loadingLink')}
              className="referral-input"
            />
            <button type="button" className="primary-button referral-copy-button" onClick={handleCopy} disabled={!referralLink}>
              {t('referral.copy')}
            </button>
          </div>
        </label>
        <p className="referral-note">{t('referral.note')}</p>
        {copyMessage && <p className="form-hint">{copyMessage}</p>}
        {copyError && <p className="form-error">{copyError}</p>}
      </div>

      {error && <p className="form-error">{error}</p>}

      <div className="referral-stats">
        <div className="referral-stat-card">
          <p className="referral-stat-label">{t('referral.stats.invited')}</p>
          <p className="referral-stat-value">{loading ? '...' : stats.invitedCount}</p>
        </div>
        <div className="referral-stat-card">
          <p className="referral-stat-label">{t('referral.stats.total')}</p>
          <p className="referral-stat-value">
            {loading ? '...' : `${formatEur.format(stats.totalReferralEur)} €`}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReferralProgramPage;
